<template>
  <div v-if="canShowPurchaseAgain" ref="rowRef" class="wc-purchase-again">
    <WCProductRowModern
      v-if="products.length > 0"
      rowId="purchase-again"
      :rowName="t('purchaseAgainRowTitle')"
      :items="products"
      :totalCount="totalCount"
      seeAllRoute="/me/items"
      :entryMethod="entryMethod"
    />
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, defineProps, inject, onMounted, ref, withDefaults } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import WCProductRowModern from '@/components/WCProductRow/WCProductRowModern.vue';
import AnalyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';
import { ENTRY_METHOD, type EntryMethod } from '@/models/entities/cart/entry-method';
import { configurationKey } from '@/injections';
import usePlatform from '@/modules/platform/composables/usePlatform';
import useCardLimitInRow from '@/composables/useCardLimitInRow';
import type ItemDetail from '@/models/entities/items/item-detail';

interface Props {
  entryMethod?: EntryMethod;
  historyWindowDays?: number;
  rowSizeLimit?: number;
}

const props = withDefaults(defineProps<Props>(), {
  entryMethod: ENTRY_METHOD.RECOMMENDATION_PURCHASE_AGAIN,
  historyWindowDays: 90,
  rowSizeLimit: 12,
});

const store = useStore();
const { t } = useI18n();
const { platform } = usePlatform();
const configuration = inject(configurationKey);

const isGuest = computed(() => store.getters['user/isGuest']);
const getUser = computed(() => store.getters['user/getUser']);
const canShowPurchaseAgain = computed(
  () => !isGuest.value && Boolean(configuration?.pastItemsEnabled),
);

const products = ref<ItemDetail[]>([]);
const totalCount = ref(0);
const rowRef = ref<HTMLElement | null>(null);
const getCardLimitInRow = useCardLimitInRow(rowRef, () => props.rowSizeLimit);

const fetchPurchaseAgainItems = async (): Promise<void> => {
  try {
    const ps = platform.value.isMobile ? props.rowSizeLimit : getCardLimitInRow();
    const response = await axios.post('api/me/items', {
      hs: props.historyWindowDays,
      pn: 1,
      ps,
      s: '',
    });

    if (response.data.code !== 0) {
      return;
    }

    const items = response.data.data
      .map((row: { item: ItemDetail }) => row.item)
      .filter((item: ItemDetail) => !item.discontinued);

    products.value = items;
    totalCount.value = response.data.totalCount ?? items.length;

    if (items.length > 0) {
      AnalyticsService.track(TrackableEventConstants.ItemImpression, {
        items,
        customerId: getUser.value?.sub,
        listId: 'purchase-again',
        listName: t('purchaseAgainRowTitle'),
      });
    }
  } catch (error) {
    console.error(error);
  }
};

onMounted(() => {
  if (!canShowPurchaseAgain.value) {
    return;
  }

  fetchPurchaseAgainItems();
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-purchase-again {
  :deep(.item-row .product-row-class-desktop) {
    padding: 0 2rem;
  }
}
</style>
